import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { tournamentAPI } from '../services/api';
import './TournamentFeedback.css';


function TournamentFeedback({ 
  feedback = [], 
  feedbackByPlayer = {}, 
  onUpdateFeedbackStatus, 
  onBulkUpdateStatus,
  players = [] 
}) {
  const { tournamentId } = useParams();
  const { tournament, setTournament, setOnEditTournament } = useOutletContext();
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    playerGhin: 'all',
    phase: 'all'
  });
  const [selectedFeedbackIds, setSelectedFeedbackIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Tournament editing state
  const [showTournamentEdit, setShowTournamentEdit] = useState(false);
  const [editingTournament, setEditingTournament] = useState({});

  // Filter feedback based on current filters
  const filteredFeedback = useMemo(() => {
    let filtered = [...feedback];

    if (filters.status !== 'all') {
      filtered = filtered.filter(item => item.status === filters.status);
    }

    if (filters.type !== 'all') {
      filtered = filtered.filter(item => item.type === filters.type);
    }

    if (filters.playerGhin !== 'all') {
      filtered = filtered.filter(item => item.playerGhin === filters.playerGhin);
    }

    if (filters.phase !== 'all') {
      filtered = filtered.filter(item => (item.feedbackPhase || 'pre-round') === filters.phase);
    }

    // Sort by timestamp (newest first)
    return filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [feedback, filters]);

  // Set up the edit tournament handler
  useEffect(() => {
    if (setOnEditTournament) {
      setOnEditTournament(() => handleEditTournament);
    }
    
    // Cleanup function to clear the handler when component unmounts
    return () => {
      if (setOnEditTournament) {
        setOnEditTournament(null);
      }
    };
  }, [setOnEditTournament]);

  // Tournament editing handlers
  const handleEditTournament = () => {
    setEditingTournament({
      name: tournament?.name || '',
      status: tournament?.status || 'draft',
      feedbackDeadline: tournament?.feedbackDeadline || '',
      feedbackGracePeriodMinutes: tournament?.feedbackGracePeriodMinutes || 15
    });
    setShowTournamentEdit(true);
  };

  const handleSaveTournamentSettings = async () => {
    try {
      const updateData = {
        name: editingTournament.name?.trim() || tournament?.name,
        status: editingTournament.status,
        feedbackDeadline: editingTournament.feedbackDeadline || null,
        feedbackGracePeriodMinutes: parseInt(editingTournament.feedbackGracePeriodMinutes) || 15
      };

      const response = await tournamentAPI.update(tournamentId, updateData);
      if (response.data) {
        setTournament(response.data);
        setShowTournamentEdit(false);
      }
    } catch (err) {
      console.error('Error updating tournament:', err);
    }
  };

  const handleCancelTournamentEdit = () => {
    setShowTournamentEdit(false);
  };

  // Get unique values for filter dropdowns
  const uniqueTypes = [...new Set(feedback.map(item => item.type))];
  const uniquePlayers = [...new Set(feedback.map(item => item.playerGhin))];

  // Get feedback statistics
  const feedbackStats = useMemo(() => {
    const stats = {
      total: feedback.length,
      pending: feedback.filter(item => item.status === 'pending').length,
      addressed: feedback.filter(item => item.status === 'addressed').length,
      ignored: feedback.filter(item => item.status === 'ignored').length,
      byType: {}
    };

    uniqueTypes.forEach(type => {
      stats.byType[type] = feedback.filter(item => item.type === type).length;
    });

    return stats;
  }, [feedback, uniqueTypes]);

  const handleStatusUpdate = async (feedbackId, newStatus) => {
    if (!onUpdateFeedbackStatus) return;
    
    try {
      setIsLoading(true);
      await onUpdateFeedbackStatus(feedbackId, newStatus);
    } catch (error) {
      console.error('Error updating feedback status:', error);
      setError('Failed to update feedback status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedFeedbackIds.size === 0 || !onBulkUpdateStatus) return;

    try {
      setIsLoading(true);
      await onBulkUpdateStatus(Array.from(selectedFeedbackIds), bulkAction);
      setSelectedFeedbackIds(new Set());
      setBulkAction('');
    } catch (error) {
      console.error('Error performing bulk action:', error);
      setError('Failed to perform bulk action');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedFeedbackIds.size === filteredFeedback.length) {
      setSelectedFeedbackIds(new Set());
    } else {
      setSelectedFeedbackIds(new Set(filteredFeedback.map(item => item._id)));
    }
  };

  const handleSelectFeedback = (feedbackId) => {
    const newSelected = new Set(selectedFeedbackIds);
    if (newSelected.has(feedbackId)) {
      newSelected.delete(feedbackId);
    } else {
      newSelected.add(feedbackId);
    }
    setSelectedFeedbackIds(newSelected);
  };

  const getPlayerName = (ghin) => {
    const player = players.find(p => p.ghin === ghin);
    return player?.name || ghin;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ffd54f';
      case 'addressed': return '#81c784';
      case 'ignored': return '#e57373';
      default: return '#f5f5f5';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'time': return '⏰';
      case 'partner': return '👥';
      case 'pairing': return '🤝';
      case 'general': return '💬';
      default: return '📝';
    }
  };

  if (tournamentId === 'demo') {
    return (
      <div className="tournament-feedback">
        <div className="feedback-header">
          <div className="feedback-info">
            <h2>Feedback Review</h2>
            <p className="feedback-description">This is a demo mode - feedback features are not available</p>
          </div>
        </div>
        
        <div className="feedback-content">
          <div className="demo-notice">
            <h3>Demo Mode</h3>
            <p>Feedback management is available in real tournaments. Players can submit feedback about their tee time assignments, and tournament directors can review and respond to comments here.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tournament-feedback">
      <div className="feedback-header">
        <div className="feedback-info">
          <h2>Feedback Review</h2>
          <p className="feedback-count">
            {filteredFeedback.length} of {feedback.length} comments
          </p>
        </div>
        
        <div className="feedback-stats">
          <div className="stat-item pending">
            <span className="stat-value">{feedbackStats.pending}</span>
            <span className="stat-label">Pending</span>
          </div>
          <div className="stat-item addressed">
            <span className="stat-value">{feedbackStats.addressed}</span>
            <span className="stat-label">Addressed</span>
          </div>
          <div className="stat-item ignored">
            <span className="stat-value">{feedbackStats.ignored}</span>
            <span className="stat-label">Ignored</span>
          </div>
        </div>
      </div>

      <div className="feedback-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>Status:</label>
            <select 
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="addressed">Addressed</option>
              <option value="ignored">Ignored</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Type:</label>
            <select 
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
            >
              <option value="all">All Types</option>
              {uniqueTypes.map(type => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Player:</label>
            <select
              value={filters.playerGhin}
              onChange={(e) => setFilters(prev => ({ ...prev, playerGhin: e.target.value }))}
            >
              <option value="all">All Players</option>
              {uniquePlayers.map(ghin => (
                <option key={ghin} value={ghin}>
                  {getPlayerName(ghin)}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Phase:</label>
            <select
              value={filters.phase}
              onChange={(e) => setFilters(prev => ({ ...prev, phase: e.target.value }))}
            >
              <option value="all">All Phases</option>
              <option value="pre-round">Pre-Round</option>
              <option value="post-round">Post-Round</option>
            </select>
          </div>
        </div>

        {filteredFeedback.length > 0 && (
          <div className="bulk-actions">
            <label>
              <input
                type="checkbox"
                checked={selectedFeedbackIds.size === filteredFeedback.length && filteredFeedback.length > 0}
                onChange={handleSelectAll}
              />
              Select All
            </label>
            
            {selectedFeedbackIds.size > 0 && (
              <div className="bulk-action-controls">
                <select 
                  value={bulkAction}
                  onChange={(e) => setBulkAction(e.target.value)}
                >
                  <option value="">Bulk Action...</option>
                  <option value="addressed">Mark as Addressed</option>
                  <option value="ignored">Mark as Ignored</option>
                  <option value="pending">Mark as Pending</option>
                </select>
                <button 
                  onClick={handleBulkAction}
                  disabled={!bulkAction || isLoading}
                  className="bulk-action-btn"
                >
                  {isLoading ? 'Processing...' : `Apply (${selectedFeedbackIds.size})`}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="feedback-content">
        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {filteredFeedback.length === 0 ? (
          <div className="no-feedback">
            <div className="no-feedback-icon">📝</div>
            <h3>No Feedback</h3>
            <p>
              {feedback.length === 0 
                ? "No feedback has been submitted for this tournament yet."
                : "No feedback matches the current filters."
              }
            </p>
            {filters.status !== 'all' || filters.type !== 'all' || filters.playerGhin !== 'all' || filters.phase !== 'all' ? (
              <button
                onClick={() => setFilters({ status: 'all', type: 'all', playerGhin: 'all', phase: 'all' })}
                className="clear-filters-btn"
              >
                Clear Filters
              </button>
            ) : null}
          </div>
        ) : (
          <div className="feedback-list">
            {filteredFeedback.map(item => (
              <div key={item._id} className={`feedback-item status-${item.status}`}>
                <div className="feedback-item-header">
                  <label className="feedback-select">
                    <input
                      type="checkbox"
                      checked={selectedFeedbackIds.has(item._id)}
                      onChange={() => handleSelectFeedback(item._id)}
                    />
                  </label>
                  
                  <div className="feedback-meta">
                    <span className="feedback-type">
                      {getTypeIcon(item.type)} {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                    </span>
                    <span className="feedback-player">
                      {getPlayerName(item.playerGhin)}
                    </span>
                    <span className="feedback-timestamp">
                      {new Date(item.timestamp).toLocaleString()}
                    </span>
                  </div>

                  <div className="feedback-status-control">
                    <select
                      value={item.status}
                      onChange={(e) => handleStatusUpdate(item._id, e.target.value)}
                      className={`status-select status-${item.status}`}
                      style={{ backgroundColor: getStatusColor(item.status) }}
                      disabled={isLoading}
                    >
                      <option value="pending">Pending</option>
                      <option value="addressed">Addressed</option>
                      <option value="ignored">Ignored</option>
                    </select>
                  </div>
                </div>

                <div className="feedback-content-text">
                  {item.rating && (
                    <div className="feedback-rating">
                      {'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)}
                    </div>
                  )}
                  <p>{item.content}</p>
                  {item.feedbackPhase === 'post-round' && (
                    <span className="feedback-phase-badge post-round">Post-Round</span>
                  )}
                </div>

                {item.version && (
                  <div className="feedback-version">
                    Tournament Version: {item.version}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tournament Settings Modal */}
      {showTournamentEdit && (
        <div className="modal-overlay">
          <div className="tournament-settings-modal">
            <h3>Tournament Settings</h3>
            
            <div className="form-group">
              <label htmlFor="tournament-name">Tournament Name:</label>
              <input
                type="text"
                id="tournament-name"
                value={editingTournament.name || ''}
                onChange={(e) => setEditingTournament(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>


            <div className="form-group">
              <label htmlFor="tournament-status">Status:</label>
              <select
                id="tournament-status"
                value={editingTournament.status || ''}
                onChange={(e) => setEditingTournament(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="draft">Draft</option>
                <option value="processing">Processing</option>
                <option value="published-for-feedback">Published for Feedback</option>
                <option value="completed">Completed</option>
                <option value="finalized">Finalized</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="feedback-deadline">Feedback Deadline:</label>
              <input
                type="datetime-local"
                id="feedback-deadline"
                value={editingTournament.feedbackDeadline || ''}
                onChange={(e) => setEditingTournament(prev => ({ ...prev, feedbackDeadline: e.target.value }))}
              />
              <small>Leave empty for no deadline</small>
            </div>

            <div className="form-group">
              <label htmlFor="grace-period">Grace Period (minutes):</label>
              <input
                type="number"
                id="grace-period"
                min="0"
                max="60"
                value={editingTournament.feedbackGracePeriodMinutes || 15}
                onChange={(e) => setEditingTournament(prev => ({ ...prev, feedbackGracePeriodMinutes: e.target.value }))}
              />
              <small>How long to allow feedback after the deadline</small>
            </div>

            <div className="modal-actions">
              <button onClick={handleCancelTournamentEdit} className="cancel-button">
                Cancel
              </button>
              <button onClick={handleSaveTournamentSettings} className="confirm-button">
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TournamentFeedback;